import { Skeleton, Stack, useTheme } from "@mui/material";
import { Text, TEXT_WEIGHT } from "../../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../../core-ui/components/Text/TextUtils";
import {
  Button,
  ButtonVariant,
} from "../../../core-ui/components/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import { FacebookProfileResponseProps } from "../../../../hooks/useGetFacebookProfile";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useMemo } from "react";

interface SocialMediaConnectProps {
  handleFacebookAuth: () => void;
  isFetchingFacebookLoginStatus: boolean;
  facebookProfile: FacebookProfileResponseProps | undefined;
  isLoggingInFacebook: boolean;
  isLoggingOutFacebook: boolean;
  isFetchingFacebookProfile: boolean;
}

export const SocialMediaConnect = ({
  facebookProfile,
  handleFacebookAuth,
  isFetchingFacebookLoginStatus,
  isLoggingInFacebook,
  isLoggingOutFacebook,
  isFetchingFacebookProfile,
}: SocialMediaConnectProps) => {
  const theme = useTheme();

  const facebookConnectionSection = useMemo(() => {
    if (isFetchingFacebookLoginStatus) {
      return <Skeleton variant="rounded" width="100%" height={20} />;
    }

    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5}>
          <Button
            startIcon={
              <FontAwesomeIcon
                icon={faFacebook}
                style={{ color: theme.palette.standardColor.Blue[600] }}
              />
            }
            sx={{
              alignSelf: "flex-start",
              fontWeight: "normal",
              width: "165px",
            }}
            variant={ButtonVariant.OUTLINED}
            onClick={handleFacebookAuth}
            loading={
              isLoggingInFacebook ||
              isLoggingOutFacebook ||
              isFetchingFacebookProfile
            }
          >
            {facebookProfile ? "Unlink" : "Link"} Facebook
          </Button>
          {facebookProfile && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={(theme) => ({
                color: theme.palette.success.main,
              })}
            >
              <FontAwesomeIcon icon={faCheck} />
              <Text
                sx={(theme) => ({
                  color: theme.palette.success.main,
                })}
              >
                Connected
              </Text>
            </Stack>
          )}
        </Stack>
        {facebookProfile && (
          <Text variant={TextStyleVariant.P2} color={TextColor.SECONDARY}>
            Signed in using SSO:{" "}
            {facebookProfile?.email ||
              facebookProfile?.name ||
              facebookProfile?.id}
          </Text>
        )}
      </Stack>
    );
  }, [
    facebookProfile,
    handleFacebookAuth,
    isFetchingFacebookLoginStatus,
    isFetchingFacebookProfile,
    isLoggingInFacebook,
    isLoggingOutFacebook,
    theme.palette.standardColor.Blue,
  ]);

  return (
    <Stack spacing={2}>
      <Text variant={TextStyleVariant.P1} weight={TEXT_WEIGHT.SEMI_BOLD}>
        Connect social media accounts
      </Text>
      {facebookConnectionSection}
    </Stack>
  );
};
